import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  CaptionText,
  FlexColumn,
  Input,
  SearchIcon,
  Size,
  SvgIcon,
  useClickAway,
} from '@make-software/cspr-design';
import { BodyText, Button } from '@block-explorer/ui';

import { buildRouterPath, useCustomHistory } from '../../router/utils';
import { useSearch } from '../../hooks/use-search';
import { Trans, useTranslation } from 'react-i18next';
import { SEARCH_PATH, SEARCH_RESULTS_PATH } from '../../router/paths';
import { SearchDropdown } from './search-dropdown';
import {
  SHORTENED_ENTRY_TYPE_LENGTH,
  truncateName,
} from '../action-messages/utils';
import { canSearchByNameTerm } from '../../hooks/search-utils';

const StyledContainer = styled.div({
  position: 'relative',
  width: '100%',
});

const SearchButton = styled(Button)(({ theme }) => ({
  padding: 0,
  position: 'absolute',
  right: 0,
  top: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  minWidth: 40,
  margin: 0,
}));

const StyledInput = styled(Input)(({ theme }) => ({
  paddingRight: 35,
}));

const NothingFoundMenu = styled(FlexColumn)<
  Pick<SearchInputProps, 'top' | 'width'>
>(({ theme, top, width }) =>
  theme.withMedia({
    width: width,
    background: theme.styleguideColors.backgroundPrimary,
    borderRadius: theme.borderRadius.base,
    position: 'absolute',
    top: top,
    height: 149,
    zIndex: theme.zIndex.dropdown,
    boxShadow: theme.boxShadow.tooltip,
    justifyContent: 'center',
    alignItems: 'center',

    path: {
      fill: theme.styleguideColors.fillPrimaryRed,
    },
  })
);

export interface SearchInputProps {
  className?: string;
  innerRef?: any;
  disabled?: boolean;
  required?: boolean;
  size?: Size;
  placeholder?: string;
  value?: any;
  onChange?: (ev: any) => void;
  onKeyDown?: (ev: any) => void;
  top?: (string | number)[];
  width?: (string | number)[];
}

export function SearchInput({ top, width, ...props }: SearchInputProps) {
  const { t } = useTranslation();
  const history = useCustomHistory();

  const [searchValue, setSearchValue] = useState('');
  const [showNotFoundMenu, setShowNotFoundBlock] = useState(false);
  const [toggleDropdown, setToggleDropdown] = useState(false);

  const closeDropdown = () => {
    setToggleDropdown(true);
    setShowNotFoundBlock(false);
  };

  const { ref } = useClickAway({
    callback: () => {
      closeDropdown();
    },
  });

  const { data, error, loading } = useSearch(searchValue);
  const searchTerm = searchValue.trim().toLowerCase();

  const showNamedResultsDropdown =
    !toggleDropdown &&
    canSearchByNameTerm(searchValue) &&
    (loading || data?.type === 'named-search');

  useEffect(() => {
    if (!loading && error) {
      setShowNotFoundBlock(true);
    }
  }, [loading, error]);

  const handleAllResultsClick = () => {
    closeDropdown();
    history.push(
      buildRouterPath(SEARCH_RESULTS_PATH, undefined, {
        search: searchTerm,
      })
    );
  };

  const handleSearchSubmit = () => {
    if (searchTerm === '' || loading) {
      return;
    }

    if (data?.type === 'named-search') {
      handleAllResultsClick();
    } else if (data?.type != null && data.hash != null) {
      closeDropdown();
      history.push(`/${data.type}/${data.hash}`);
    } else if (error) {
      setShowNotFoundBlock(true);
    } else {
      closeDropdown();
      history.push(buildRouterPath(SEARCH_PATH, { id: searchTerm }, undefined));
    }
  };

  const handleInputChange = (event) => {
    setSearchValue(event.target.value);
    setShowNotFoundBlock(false);
    setToggleDropdown(false);
  };

  return (
    <StyledContainer ref={ref}>
      <StyledInput
        height="40"
        id={'search-input-button'}
        aria-label="Search by name, public key, hash, uref or CSPR.name"
        placeholder="Search by name, public key, hash, uref or CSPR.name"
        onChange={handleInputChange}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleSearchSubmit();
          }
          if (event.key === 'Escape') {
            closeDropdown();
          }
        }}
        {...props}
        autoComplete="off"
      />
      <SearchButton
        id={'search-input-button'}
        aria-label="Submit search"
        height="40"
        type="button"
        onClick={handleSearchSubmit}
        color="primaryRed"
      >
        <SvgIcon
          src={SearchIcon}
          size={16}
          alt={t('Search button')}
          role={'img'}
        />
      </SearchButton>
      {showNamedResultsDropdown && (
        <SearchDropdown
          data={data?.results}
          loading={loading}
          onClickAllResults={handleAllResultsClick}
          onSelectItem={closeDropdown}
        />
      )}
      {showNotFoundMenu && (
        <NothingFoundMenu itemsSpacing={16} top={top} width={width}>
          <SvgIcon
            src={SearchIcon}
            size={40}
            alt={t('Search button')}
            role={'img'}
          />
          <FlexColumn align={'center'} itemsSpacing={4}>
            <BodyText size={3}>
              <Trans t={t}>
                No results for "
                {{
                  searchTerm: truncateName(
                    searchTerm,
                    SHORTENED_ENTRY_TYPE_LENGTH
                  ),
                }}
                "
              </Trans>
            </BodyText>
            <CaptionText size={2} variation={'gray'}>
              <Trans t={t}>
                Try a Name, Public Key, Hash, URef or CSPR.name
              </Trans>
            </CaptionText>
          </FlexColumn>
        </NothingFoundMenu>
      )}
    </StyledContainer>
  );
}
